import { Alert, Box, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";

interface Props {
  contactId: string;
  contactEmail: string;
  contactName: string;
  onResetCompleted: () => void;
  onClose: () => void;
}

const resetContactPassword = withErrorHandling(adminApi.resetContactPassword);

const ResetPasswordDialog = ({ contactId, contactEmail, contactName, onResetCompleted, onClose }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [resetStatus, setResetStatus] = useState<"prompt" | "success">("prompt");
  const [isLoading, setIsLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState("");

  const handleSubmit = async () => {
    setIsLoading(true);
    const [resp, error] = await resetContactPassword(contactId);
    setIsLoading(false);

    if (error) {
      sendNotificationError(error.message);
      if (error.type !== "PreconditionFailed") {
        logError(error, "[ResetPasswordDialog] resetContactPassword");
      }
      return;
    }

    if (resp.tempPassword) {
      setTempPassword(resp.tempPassword);
    }
    setResetStatus("success");
    onResetCompleted();
  };

  // The temp password is shown only once, so a backdrop click must not discard it.
  const handleDialogClose = () => {
    if (isLoading || resetStatus === "success") {
      return;
    }
    onClose();
  };

  return (
    <SmallDialog open onClose={handleDialogClose}>
      <DialogTitle>Reset Password</DialogTitle>
      {!isLoading && <DialogCloseButton onClick={onClose} />}
      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2.5}>
          <Stack spacing={1.5}>
            <Stack spacing={0.25}>
              <Typography variant="subtitle2">Reset password for:</Typography>
              <Typography>{contactName}</Typography>
              <Typography color="textSecondary">{contactEmail}</Typography>
            </Stack>
            {resetStatus === "success" && <Alert severity="success">Password has been reset</Alert>}
          </Stack>

          {resetStatus === "prompt" && (
            <Typography color="textSecondary">
              The user will be assigned a temporary password that must be changed on the next sign in. To perform the
              reset and view the temporary password, click “Reset password”.
            </Typography>
          )}

          {resetStatus === "success" && tempPassword && (
            <Stack spacing={1.5} sx={{ pt: 1.5 }}>
              <Typography color="textSecondary">
                Provide this temporary password to the user so they can sign in. It is shown only once — copy it before
                closing.
              </Typography>
              <Box
                sx={{
                  px: 1.25,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  bgcolor: "background.grey",
                  height: 50,
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1">{tempPassword}</Typography>
                <CopyToBufferButton textToCopy={tempPassword} />
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      {resetStatus === "prompt" && (
        <DialogButtons onCancel={onClose} cancelDisabled={isLoading}>
          <Button variant="contained" loading={isLoading} onClick={handleSubmit}>
            Reset Password
          </Button>
        </DialogButtons>
      )}
      {resetStatus === "success" && (
        <DialogButtons>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogButtons>
      )}
    </SmallDialog>
  );
};

export default ResetPasswordDialog;
